import { Box, Dropdown, TextArea } from '@eatclubapp/ec-component-library';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import dayjs from 'dayjs';
import { PrimaryButton } from '../../components/button/PrimaryButton';
import DatePopover from '../../components/offers/CreateOffer/DatePopover';
import { CANCEL_REASON_TRACKING } from '../../constants';
import { trackDetailedEvent } from '../../utils/analytics';
import { isEmpty, isVoid } from '../../utils/baseHelpers';
import { formatCurrency } from '../../utils/helpers';
import CancelMembershipPage from './CancelMembershipPage';
import { primaryButtonStyles } from './CancelMembershipStyles';
import useStyles, {
  textAreaStyles,
  toggleGroupStyles,
  verticalToggleGroupStyles,
} from './CancelReasonPageStyles';
import ContinueButton from './ContinueButton';
import { ToggleGroup } from './ToggleGroup/ToggleGroup';

const CancelReasonPage = ({
  currentStep,
  totalSteps,
  onContinue,
  onExit,
  reason,
  setReason,
  isSaving,
}) => {
  const classes = useStyles();

  const [referralBonus, setReferralBonus] = useState(null);
  const [newOwnerDetails, setNewOwnerDetails] = useState(null);
  const [emailAddress, setEmailAddress] = useState(null);
  const [pauseWeeks, setPauseWeeks] = useState(null);
  const [valueReasons, setValueReasons] = useState(null);
  const [reconsiderNotes, setReconsiderNotes] = useState(null);
  const [issuesNotes, setIssuesNotes] = useState(null);
  const [issueResolution, setIssueResolution] = useState(null);
  const [customDate, setCustomDate] = useState(null);
  const [notes, setNotes] = useState(null);

  const [error, setError] = useState('');

  const [dateAnchor, setDateAnchor] = useState(null);

  const today = dayjs();

  const validate = () => {
    if (isEmpty(reason)) {
      setError('You must select a reason');
      return false;
    }

    let valid = true;
    switch (reason) {
      case 'sold_business':
        if (isVoid(referralBonus)) {
          valid = false;
        }
        break;
      // case 'closed_permanently':
      //   if (isEmpty(emailAddress)) {
      //     valid = false;
      //   }
      //   break;
      case 'pause_membership':
        if (
          isEmpty(pauseWeeks) ||
          (pauseWeeks === 'custom' && isEmpty(customDate)) ||
          isEmpty(notes)
        ) {
          valid = false;
        }
        break;
      case 'not_getting_value':
        if (isEmpty(valueReasons) || isEmpty(reconsiderNotes)) {
          valid = false;
        }
        break;
      case 'financial_difficulty':
        if (isEmpty(notes)) {
          valid = false;
        }
        break;
      case 'technical_issues':
        if (isVoid(issueResolution) || isEmpty(issuesNotes)) {
          valid = false;
        }
        break;
      case 'eatclub_customer_issues':
        if (isEmpty(issuesNotes)) {
          valid = false;
        }
        break;
      default:
        break;
    }

    if (!valid) {
      setError('You must fill out all required fields');
      return false;
    }

    setError('');
    return true;
  };

  // Update the reasons in the parent component
  const handleContinue = () => {
    const isValid = validate();
    if (isValid) {
      onContinue({
        referralBonus,
        emailAddress,
        pauseWeeks,
        valueReasons,
        reconsiderNotes,
        issuesNotes,
        issueResolution,
        customDate,
        notes,
        newOwnerDetails,
      });
    }
  };

  // Reset the error when the data changes
  useEffect(() => {
    setError('');
  }, [
    reason,
    emailAddress,
    pauseWeeks,
    valueReasons,
    reconsiderNotes,
    issuesNotes,
    issueResolution,
    customDate,
    notes,
    newOwnerDetails,
  ]);

  const reasons = [
    { label: 'I’ve sold the business', value: 'sold_business' },
    { label: 'I’ve closed the business permanently', value: 'closed_permanently' },
    { label: "I'm experiencing issues with EatClub customers", value: 'eatclub_customer_issues' },
    {
      label: 'I want to pause my EatClub membership due to renovation or staff shortage',
      value: 'pause_membership',
    },
    { label: 'I’m not getting the value I expected', value: 'not_getting_value' },
    { label: 'I’m experiencing financial difficulty', value: 'financial_difficulty' },
    // { label: 'My venue is full, I don’t need extra customers', value: 'venue_full' },
    { label: 'I keep experiencing technical issues', value: 'technical_issues' },
  ];
  const referralAmount = 250;
  const voucherAmount = 50;

  const handleSetValueReasons = (newReason) => {
    setValueReasons(newReason);

    trackDetailedEvent('Secondary reason selected', {
      cancel_reason: CANCEL_REASON_TRACKING?.[reason] ?? 'None',
      secondary_reason: CANCEL_REASON_TRACKING?.[newReason] ?? 'None',
    });
  };

  const getReactivationMessage = () => {
    if (pauseWeeks === 'custom') {
      if (customDate) {
        return `Your account will re-activate on ${customDate.format('DD/MM/YYYY')}`;
      }

      return null;
    }

    if (pauseWeeks === null) {
      return null;
    }

    const reactivationTime = dayjs().add(pauseWeeks, 'weeks').format('DD/MM/YYYY');
    return `Your account will re-activate on ${reactivationTime}`;
  };

  return (
    <CancelMembershipPage
      currentStep={currentStep}
      totalSteps={totalSteps}
      isSaving={false}
      footerLeft={
        <PrimaryButton style={primaryButtonStyles} onClick={onExit} loading={isSaving}>
          Never mind, I don&apos;t want to cancel
        </PrimaryButton>
      }
      footerRight={
        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {error && <Box style={{ color: 'red', paddingBottom: '12px' }}>{error}</Box>}
          <ContinueButton onClick={handleContinue} loading={isSaving}>
            Continue to cancel
          </ContinueButton>
        </Box>
      }
    >
      <Box className={classes.mainContent}>
        <Box className={classes.title}>Okay, let us know why you want to pause your account?</Box>
        <Box className={classes.pageContent}>
          <Box className={classes.dropdownContainer}>
            <Dropdown
              label='This helps us improve and make better solutions for the community.'
              items={reasons}
              placeholder='Select reason'
              style={{
                fontSize: '16px',
                label: {
                  fontSize: '18px',
                  paddingBottom: '24px',
                },
                dropdownHover: {
                  border: '1px solid #313131',
                  filter: 'none', // The filter was breaking the icon
                },
              }}
              onSelect={setReason}
              value={reason}
            />
          </Box>

          {reason === 'sold_business' && (
            <Box className={classes.dynamicContent}>
              <ToggleGroup
                label={`Are you interested in a ${formatCurrency(referralAmount, false)} referral bonus for getting the new owner to keep using EatClub?`}
                onSelect={setReferralBonus}
                items={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                style={toggleGroupStyles}
                value={referralBonus}
                singleChoice
              />

              {referralBonus === true && (
                <TextArea
                  label='What are the contact details of the new owner?'
                  resizable={false}
                  value={newOwnerDetails}
                  onChange={setNewOwnerDetails}
                  style={textAreaStyles}
                />
              )}

              <TextArea
                label='We’d love to hear any ideas on how we can improve for venues like yours.'
                resizable={false}
                value={notes}
                onChange={setNotes}
                style={textAreaStyles}
              />
            </Box>
          )}

          {/* {reason === 'closed_permanently' && ( */}
          {/*   <Box className={classes.dynamicContent}> */}
          {/*     <Box> */}
          {/*       Let us know your personal email and we’ll send you a{' '} */}
          {/*       {formatCurrency(voucherAmount, false)} voucher for the next order you make as an */}
          {/*       EatClub customer. */}
          {/*     </Box> */}

          {/*     <TextField */}
          {/*       label='Email address' */}
          {/*       value={emailAddress} */}
          {/*       onChange={setEmailAddress} */}
          {/*       style={textFieldStyles} */}
          {/*       placeholder='email@example.com' */}
          {/*     /> */}
          {/*   </Box> */}
          {/* )} */}

          {reason === 'pause_membership' && (
            <Box className={classes.dynamicContent}>
              <Box>
                <ToggleGroup
                  label='How many weeks do you want to pause for?'
                  onSelect={(newValue, e) => {
                    setPauseWeeks(newValue);
                    if (newValue === 'custom') {
                      setDateAnchor(e?.currentTarget);
                    }
                  }}
                  items={[
                    { label: '1', value: 1 },
                    { label: '2', value: 2 },
                    { label: '3', value: 3 },
                    {
                      label: 'Select date',
                      value: 'custom',
                    },
                  ]}
                  style={{ ...toggleGroupStyles, ...{ groupOptionContainer: { width: '70px' } } }}
                  value={pauseWeeks}
                  singleChoice
                />

                <Box style={{ height: '30px', paddingTop: '12px' }}>{getReactivationMessage()}</Box>
              </Box>

              <TextArea
                label='Please tell us more'
                resizable={false}
                value={notes}
                onChange={setNotes}
                style={textAreaStyles}
              />
            </Box>
          )}

          {reason === 'not_getting_value' && (
            <Box className={classes.dynamicContent}>
              <ToggleGroup
                label='Help us understand why?'
                value={valueReasons}
                onSelect={handleSetValueReasons}
                items={[
                  {
                    label: 'I’m struggling to make enough margin after offer',
                    value: 'struggling_to_make_enough_margin_after_offer',
                  },
                  // {
                  //   label: 'Unsure of the balance of new and repeat customers',
                  //   value: 'unsure_of_new_vs_repeat_customers_balance',
                  // },
                  { label: 'I’m not getting enough orders', value: 'not_getting_enough_orders' },
                  {
                    label: 'Fees are too high, not enough value for money',
                    value: 'fees_are_too_high',
                  },
                  // { label: 'Issues with EatClub customers', value: 5 },
                ]}
                style={verticalToggleGroupStyles}
                // singleChoice={false}
                showCheckmark
              />

              <TextArea
                label='Is there any improvement we could make to re-consider pausing your account?'
                resizable={false}
                value={reconsiderNotes}
                onChange={setReconsiderNotes}
                style={textAreaStyles}
              />
            </Box>
          )}

          {reason === 'financial_difficulty' && (
            <Box className={classes.dynamicContent}>
              <TextArea
                label='We’d love to hear any ideas on how we can improve for venues like yours.'
                resizable={false}
                value={notes}
                onChange={setNotes}
                style={textAreaStyles}
              />
            </Box>
          )}

          {reason === 'technical_issues' && (
            <Box className={classes.dynamicContent}>
              <TextArea
                label='What were some of the issues?'
                resizable={false}
                value={issuesNotes}
                onChange={setIssuesNotes}
                style={textAreaStyles}
              />

              <ToggleGroup
                label='If we could resolve these issues immediately, would you re-consider staying with EatClub?'
                value={issueResolution}
                onSelect={setIssueResolution}
                items={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                style={toggleGroupStyles}
                singleChoice
              />
            </Box>
          )}

          {reason === 'eatclub_customer_issues' && (
            <Box className={classes.dynamicContent}>
              <TextArea
                label='What were some of the issues?'
                resizable={false}
                value={issuesNotes}
                onChange={setIssuesNotes}
                style={textAreaStyles}
              />
            </Box>
          )}
        </Box>
      </Box>

      <DatePopover
        value={customDate}
        anchor={dateAnchor}
        isOpen={!!dateAnchor}
        onClose={() => setDateAnchor(null)}
        onSetDate={setCustomDate}
        datePickerProps={{
          filterDate: (date) => {
            return dayjs(date).isAfter(today) && dayjs(date).isBefore(today.add(22, 'days'));
          },
        }}
      />
    </CancelMembershipPage>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CancelReasonPage);
